import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, User, GraduationCap, History, StickyNote, Users, Loader2, Plus, Edit, Trash2, BookOpen } from 'lucide-react';
import { StudentBooksCard } from '../../components/BooksFees';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyLastYear = { year: '', program_name: '', class_name: '', teacher_name: '',
    quran_curriculum: '', quran_mark: '', arabic_book: '', arabic_mark: '',
    tajweed_book: '', tajweed_mark: '', islamic_education_book: '', islamic_education_mark: '',
    absence_days: 0 };

const StudentDetail = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [student, setStudent] = useState(null);
    const [parent, setParent] = useState(null);
    const [program, setProgram] = useState(null);
    const [klass, setKlass] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('details');
    // Last year
    const [lastYearRecords, setLastYearRecords] = useState([]);
    const [lyDialog, setLyDialog] = useState(null); // form state
    // Notes
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    // Audit log
    const [audit, setAudit] = useState([]);
    // Family (siblings)
    const [family, setFamily] = useState({ active: [], waiting_list: [], unregistered: [] });

    useEffect(() => { (async () => {
        try {
            const [sRes, lyRes, notesRes, auditRes, famRes] = await Promise.all([
                axios.get(`${API_URL}/api/students/${studentId}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students/${studentId}/last-year`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students/${studentId}/notes`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students/${studentId}/audit-log`, { withCredentials: true }),
                axios.get(`${API_URL}/api/students/${studentId}/family`, { withCredentials: true }).catch(() => ({ data: { active: [], waiting_list: [], unregistered: [] } })),
            ]);
            setStudent(sRes.data);
            setLastYearRecords(lyRes.data || []);
            setNotes(notesRes.data || []);
            setAudit(auditRes.data || []);
            setFamily(famRes.data || { active: [], waiting_list: [], unregistered: [] });
            if (sRes.data?.parent_id) {
                axios.get(`${API_URL}/api/parents/${sRes.data.parent_id}`, { withCredentials: true }).then(r => setParent(r.data)).catch(() => {});
            }
            if (sRes.data?.program_id) {
                axios.get(`${API_URL}/api/programs/${sRes.data.program_id}`, { withCredentials: true }).then(r => setProgram(r.data)).catch(() => {});
            }
            if (sRes.data?.class_id) {
                axios.get(`${API_URL}/api/classes/${sRes.data.class_id}`, { withCredentials: true }).then(r => setKlass(r.data)).catch(() => {});
            }
        } catch { toast.error('Failed to load student'); }
        finally { setLoading(false); }
    })(); }, [studentId]);

    const saveLastYear = async () => {
        if (!lyDialog?.year?.trim()) { toast.error('Year is required'); return; }
        try {
            await axios.post(`${API_URL}/api/students/${studentId}/last-year`, lyDialog, { withCredentials: true });
            const r = await axios.get(`${API_URL}/api/students/${studentId}/last-year`, { withCredentials: true });
            setLastYearRecords(r.data || []);
            setLyDialog(null);
            toast.success('Last-year record saved');
        } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    };

    const deleteLastYear = async (year) => {
        if (!window.confirm(`Delete last-year record for ${year}?`)) return;
        try {
            await axios.delete(`${API_URL}/api/students/${studentId}/last-year/${encodeURIComponent(year)}`, { withCredentials: true });
            setLastYearRecords(prev => prev.filter(x => x.year !== year));
            toast.success('Deleted');
        } catch { toast.error('Delete failed'); }
    };

    const addNote = async () => {
        if (!newNote.trim()) return;
        try {
            const r = await axios.post(`${API_URL}/api/students/${studentId}/notes`, { note: newNote.trim() }, { withCredentials: true });
            setNotes(prev => [r.data, ...prev]);
            setNewNote('');
            toast.success('Note added');
        } catch { toast.error('Failed to add note'); }
    };

    const removeNote = async (nid) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            await axios.delete(`${API_URL}/api/students/${studentId}/notes/${nid}`, { withCredentials: true });
            setNotes(prev => prev.filter(n => n.id !== nid));
        } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;
    if (!student) return <DashboardLayout><div className="p-8 text-center text-[#4B5563]">Student not found</div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="student-detail-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{student.first_name} {student.last_name}</h1>
                        <p className="text-[#4B5563] text-sm">Reg # <span className="font-mono">{student.reg_code}</span></p>
                    </div>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="details" data-testid="tab-student-details"><User className="h-4 w-4 mr-2" />Details</TabsTrigger>
                        <TabsTrigger value="family" data-testid="tab-student-family"><Users className="h-4 w-4 mr-2" />Family ({family.active.length + family.waiting_list.length + family.unregistered.length})</TabsTrigger>
                        <TabsTrigger value="last-year" data-testid="tab-student-last-year"><History className="h-4 w-4 mr-2" />Last Year</TabsTrigger>
                        <TabsTrigger value="books" data-testid="tab-student-books"><BookOpen className="h-4 w-4 mr-2" />Books</TabsTrigger>
                        <TabsTrigger value="log" data-testid="tab-student-log"><GraduationCap className="h-4 w-4 mr-2" />Log</TabsTrigger>
                        <TabsTrigger value="notes" data-testid="tab-student-notes"><StickyNote className="h-4 w-4 mr-2" />Notes ({notes.length})</TabsTrigger>
                    </TabsList>

                    {/* --- Details tab --- */}
                    <TabsContent value="details">
                        <Card className="card-surface"><CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <Info label="Full Name (EN)" value={`${student.first_name} ${student.last_name}`} />
                            {(student.first_name_ar || student.last_name_ar) && <Info label="Full Name (AR)" value={<span dir="rtl">{student.first_name_ar} {student.last_name_ar}</span>} />}
                            <Info label="Date of Birth" value={student.date_of_birth} />
                            <Info label="Gender" value={student.gender} />
                            <Info label="Program" value={program?.name || student.program_id} />
                            <Info label="Class" value={klass?.arabic_name || klass?.english_name || klass?.name || '—'} />
                            <Info label="Status" value={<Badge variant={student.status === 'active' ? 'default' : 'secondary'}>{student.status}</Badge>} />
                            <Info label="Renewed for next year" value={
                                <div className="flex items-center gap-2" data-testid="renewed-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={!!student.renewed_for_next_year}
                                        onChange={async (e) => {
                                            const next = e.target.checked;
                                            try {
                                                const endpoint = next
                                                    ? `${API_URL}/api/didnt-renew/mark-renewed`
                                                    : `${API_URL}/api/didnt-renew/mark-unrenewed`;
                                                await axios.post(endpoint, { student_ids: [student.id] }, { withCredentials: true });
                                                setStudent(s => ({ ...s, renewed_for_next_year: next }));
                                                toast.success(next ? 'Marked as renewed' : 'Marked as not renewed');
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Failed to update renewal status');
                                            }
                                        }}
                                        className="h-4 w-4"
                                        data-testid="renewed-checkbox"
                                    />
                                    <span className="text-xs text-[#6B7280]">{student.renewed_for_next_year ? 'Yes' : 'No'}</span>
                                </div>
                            } />
                            <Info label="Address" value={student.address} />
                            {parent && <Info label="Guardian" value={<button className="text-[#064E3B] underline" onClick={() => navigate(`/admin/parents/${parent.id}`)}>{parent.first_name} {parent.last_name} ({parent.reg_code})</button>} />}
                        </CardContent></Card>
                    </TabsContent>

                    {/* --- Family tab (siblings across all statuses) --- */}
                    <TabsContent value="family">
                        <div className="space-y-4" data-testid="family-tab-content">
                            {[
                                { key: 'active', title: 'Active', color: '#10B981', data: family.active },
                                { key: 'waiting_list', title: 'Waiting List', color: '#F59E0B', data: family.waiting_list },
                                { key: 'unregistered', title: 'Unregistered / Discontinued', color: '#6B7280', data: family.unregistered },
                            ].map(section => (
                                <Card key={section.key} className="card-surface">
                                    <CardHeader><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><Users className="h-4 w-4" style={{ color: section.color }} />{section.title} ({section.data.length})</CardTitle></CardHeader>
                                    <CardContent className="p-0">
                                        {section.data.length === 0 ? (
                                            <p className="p-6 text-center text-[#6B7280] text-sm">None</p>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <Table>
                                                    <TableHeader><TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>DOB</TableHead>
                                                        <TableHead>Program</TableHead>
                                                        <TableHead>Class</TableHead>
                                                        <TableHead>Teacher</TableHead>
                                                    </TableRow></TableHeader>
                                                    <TableBody>
                                                        {section.data.map(sib => (
                                                            <TableRow key={sib.id} data-testid={`family-row-${sib.id}`}>
                                                                <TableCell>
                                                                    <button
                                                                        className="text-[#064E3B] underline font-medium"
                                                                        onClick={() => navigate(`/admin/students/${sib.id}`)}
                                                                        data-testid={`family-student-link-${sib.id}`}
                                                                    >{sib.first_name} {sib.last_name}</button>
                                                                    {(sib.first_name_ar || sib.last_name_ar) && (
                                                                        <div className="text-xs text-[#6B7280]" dir="rtl">{sib.first_name_ar} {sib.last_name_ar}</div>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="tabular-nums text-sm">{sib.date_of_birth || '—'}</TableCell>
                                                                <TableCell className="text-sm">{sib.program_name || '—'}</TableCell>
                                                                <TableCell className="text-sm">{sib.class_name || '—'}</TableCell>
                                                                <TableCell className="text-sm">
                                                                    {sib.teacher_id ? (
                                                                        <button className="text-[#064E3B] underline" onClick={() => navigate(`/admin/teachers?highlight=${sib.teacher_id}`)}>{sib.teacher_name}</button>
                                                                    ) : (sib.teacher_name || '—')}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* --- Last Year tab --- */}
                    <TabsContent value="books"><StudentBooksCard studentId={studentId} /></TabsContent>
                    <TabsContent value="last-year">
                        <Card className="card-surface"><CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base font-['Outfit']">Academic Record — Previous Years</CardTitle>
                            <Button size="sm" className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={() => setLyDialog({ ...emptyLastYear })} data-testid="add-last-year-btn"><Plus className="h-4 w-4 mr-1" />Add Year</Button>
                        </CardHeader>
                        <CardContent className="p-0">
                            {lastYearRecords.length === 0 ? <p className="p-6 text-center text-[#6B7280] text-sm">No academic records yet. Add a year, or upload via Excel from Students → Bulk Upload.</p> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <TableHead>Year</TableHead><TableHead>Program</TableHead><TableHead>Class</TableHead><TableHead>Teacher</TableHead>
                                            <TableHead>Quran</TableHead><TableHead>Arabic</TableHead>
                                            <TableHead>Tajweed</TableHead><TableHead>Islamic Ed.</TableHead>
                                            <TableHead>Absences</TableHead><TableHead className="text-right">Actions</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {lastYearRecords.map(ly => (
                                                <TableRow key={ly.year} data-testid={`ly-row-${ly.year}`}>
                                                    <TableCell className="font-medium">{ly.year}</TableCell>
                                                    <TableCell className="text-sm">{ly.program_name || '—'}</TableCell>
                                                    <TableCell className="text-sm">{ly.class_name || '—'}</TableCell>
                                                    <TableCell className="text-sm">{ly.teacher_name || '—'}</TableCell>
                                                    <TableCell className="text-sm"><div>{ly.quran_curriculum || '—'}</div><div className="text-xs text-[#6B7280]">{ly.quran_mark || ''}</div></TableCell>
                                                    <TableCell className="text-sm"><div>{ly.arabic_book || '—'}</div><div className="text-xs text-[#6B7280]">{ly.arabic_mark || ''}</div></TableCell>
                                                    <TableCell className="text-sm"><div>{ly.tajweed_book || '—'}</div><div className="text-xs text-[#6B7280]">{ly.tajweed_mark || ''}</div></TableCell>
                                                    <TableCell className="text-sm"><div>{ly.islamic_education_book || '—'}</div><div className="text-xs text-[#6B7280]">{ly.islamic_education_mark || ''}</div></TableCell>
                                                    <TableCell className="tabular-nums">{ly.absence_days || 0}</TableCell>
                                                    <TableCell className="text-right space-x-1">
                                                        <Button size="sm" variant="ghost" onClick={() => setLyDialog({ ...ly })}><Edit className="h-4 w-4" /></Button>
                                                        <Button size="sm" variant="ghost" onClick={() => deleteLastYear(ly.year)}><Trash2 className="h-4 w-4 text-[#EF4444]" /></Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent></Card>
                    </TabsContent>

                    {/* --- Log tab --- */}
                    <TabsContent value="log">
                        <Card className="card-surface"><CardContent className="p-0">
                            {audit.length === 0 ? <p className="p-6 text-center text-[#6B7280] text-sm">No changes logged yet.</p> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader><TableRow>
                                            <TableHead>When</TableHead><TableHead>Field</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>By</TableHead>
                                        </TableRow></TableHeader>
                                        <TableBody>
                                            {audit.map(e => (
                                                <TableRow key={e.id}>
                                                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(e.created_at)}</TableCell>
                                                    <TableCell className="text-sm font-mono">{e.field}</TableCell>
                                                    <TableCell className="text-xs text-[#6B7280]">{e.old_value || '—'}</TableCell>
                                                    <TableCell className="text-xs text-[#1F2937]">{e.new_value || '—'}</TableCell>
                                                    <TableCell className="text-xs">{e.actor_name} <span className="text-[#6B7280]">({e.actor_role})</span></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent></Card>
                    </TabsContent>

                    {/* --- Notes tab --- */}
                    <TabsContent value="notes">
                        <Card className="card-surface"><CardContent className="p-5 space-y-4">
                            <div className="space-y-2">
                                <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Write a note (visible only to staff, not to parents)…" rows={3} data-testid="new-note-input" />
                                <div className="flex justify-end">
                                    <Button className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={addNote} disabled={!newNote.trim()} data-testid="add-note-btn">
                                        <Plus className="h-4 w-4 mr-1" />Add Note
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {notes.length === 0 ? <p className="text-center text-[#6B7280] text-sm py-6">No notes yet.</p> : notes.map(n => (
                                    <div key={n.id} className="border border-[#E5E7EB] bg-[#FDFCFA] rounded-lg p-3" data-testid={`note-${n.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-sm whitespace-pre-wrap flex-1">{n.note}</p>
                                            <Button size="sm" variant="ghost" onClick={() => removeNote(n.id)}><Trash2 className="h-4 w-4 text-[#EF4444]" /></Button>
                                        </div>
                                        <p className="text-xs text-[#6B7280] mt-2">{n.author_name} <span className="capitalize">({n.author_role})</span> · {formatDateTime(n.created_at)}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Add/Edit Last Year dialog */}
            <Dialog open={!!lyDialog} onOpenChange={(o) => !o && setLyDialog(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit']">Academic Record</DialogTitle>
                        <DialogDescription>Marks use format &quot;83/100&quot; or &quot;32/50&quot;.</DialogDescription>
                    </DialogHeader>
                    {lyDialog && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <F label="Year *"><Input value={lyDialog.year} onChange={(e) => setLyDialog({ ...lyDialog, year: e.target.value })} placeholder="2024-2025" data-testid="ly-year" /></F>
                            <F label="Program"><Input value={lyDialog.program_name || ''} onChange={(e) => setLyDialog({ ...lyDialog, program_name: e.target.value })} /></F>
                            <F label="Class name"><Input value={lyDialog.class_name} onChange={(e) => setLyDialog({ ...lyDialog, class_name: e.target.value })} /></F>
                            <F label="Teacher name"><Input value={lyDialog.teacher_name} onChange={(e) => setLyDialog({ ...lyDialog, teacher_name: e.target.value })} /></F>
                            <F label="Absence days"><Input type="number" value={lyDialog.absence_days} onChange={(e) => setLyDialog({ ...lyDialog, absence_days: parseInt(e.target.value) || 0 })} /></F>
                            <F label="Quran curriculum"><Input value={lyDialog.quran_curriculum} onChange={(e) => setLyDialog({ ...lyDialog, quran_curriculum: e.target.value })} /></F>
                            <F label="Quran mark"><Input value={lyDialog.quran_mark} onChange={(e) => setLyDialog({ ...lyDialog, quran_mark: e.target.value })} placeholder="83/100" /></F>
                            <F label="Arabic book"><Input value={lyDialog.arabic_book} onChange={(e) => setLyDialog({ ...lyDialog, arabic_book: e.target.value })} /></F>
                            <F label="Arabic mark"><Input value={lyDialog.arabic_mark} onChange={(e) => setLyDialog({ ...lyDialog, arabic_mark: e.target.value })} /></F>
                            <F label="Tajweed book"><Input value={lyDialog.tajweed_book} onChange={(e) => setLyDialog({ ...lyDialog, tajweed_book: e.target.value })} /></F>
                            <F label="Tajweed mark"><Input value={lyDialog.tajweed_mark} onChange={(e) => setLyDialog({ ...lyDialog, tajweed_mark: e.target.value })} /></F>
                            <F label="Islamic Ed. book"><Input value={lyDialog.islamic_education_book} onChange={(e) => setLyDialog({ ...lyDialog, islamic_education_book: e.target.value })} /></F>
                            <F label="Islamic Ed. mark"><Input value={lyDialog.islamic_education_mark} onChange={(e) => setLyDialog({ ...lyDialog, islamic_education_mark: e.target.value })} /></F>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLyDialog(null)}>Cancel</Button>
                        <Button className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={saveLastYear} data-testid="save-last-year">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};

const Info = ({ label, value }) => (
    <div><p className="text-xs text-[#6B7280]">{label}</p><p className="text-[#1F2937]">{value || '—'}</p></div>
);
const F = ({ label, children }) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>
);

export default StudentDetail;
